import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getObservableFromAsyncValueSafe } from '../../../../models/async-value';
import { hasRequiredField } from '../../../../utils/has-required-field';
import { FieldConfig } from '../../models/field-configs/field-config.interface';

@Component({
  selector: 'dynamic-container',
  styleUrls: ['dynamic-container.component.scss'],
  templateUrl: 'dynamic-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // tslint:disable-next-line:use-host-property-decorator
  // host: {
  //   class: 'dynamic-field',
  // },
})
export class DynamicContainerComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private host: ElementRef<HTMLElement>,
    private rdr2: Renderer2,
  ) {}

  @Input()
  public config: FieldConfig;

  @Input()
  public formGroup: UntypedFormGroup;

  @HostBinding('attr.data-tid')
  @HostBinding('attr.data-test')
  public get dataTestId() {
    return this.config.name;
  }

  private _isVisible = true;
  @HostBinding('class.dynamic-field--visible')
  @HostBinding('class.d-block')
  public get isVisible() {
    return this._isVisible;
  }

  @HostBinding('class.dynamic-field--hidden')
  @HostBinding('class.d-none')
  public get isHidden() {
    return !this._isVisible;
  }

  private _isRequired = false;
  @HostBinding('class.dynamic-field--required')
  public get isRequired() {
    return this._isRequired;
  }

  @HostBinding('class.dynamic-field--invalid')
  public get isInvalid() {
    return this.control.invalid;
  }

  @HostBinding('class.dynamic-field--valid')
  public get isValid() {
    return !this.control.invalid;
  }

  private _isDisabled = false;
  @HostBinding('class.dynamic-field--disabled')
  public get isDisabled() {
    return this._isDisabled || this._isInformative;
  }

  private _isInformative = false;
  @HostBinding('class.dynamic-field--informative')
  public get isInformative() {
    return this._isInformative;
  }

  public get control() {
    return this.formGroup.get(this.config.name);
  }

  public ngOnInit() {
    this.rdr2.addClass(this.host.nativeElement, `${this.config.name}-container`);
    this._isInformative = this.config.hasOwnProperty('informative') ? this.config.informative : false;

    this._isRequired = hasRequiredField(this.control);
    if (this.config.hasOwnProperty('visible')) {
      getObservableFromAsyncValueSafe(this.config.visible)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isVisible) => {
          this._isVisible = isVisible;
        });
    }

    if (this.config.hasOwnProperty('disabled')) {
      getObservableFromAsyncValueSafe(this.config.disabled)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isDisabled) => {
          this._isDisabled = isDisabled;
        });
    }

    if (this.config.hasOwnProperty('asyncValidators')) {
      this.control.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this._isRequired = hasRequiredField(this.control);
      });
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
